import { create } from 'zustand';
import { toast } from 'sonner';
import type { FieldMapping, WebhookConfigSetting, WorkflowRule, AppPreferences, IncomingCaptureSettings, EventIncomingCaptureSettings, IncomingSyncSaasConfig, WooCommerceNotificationRule, ForwardingWebhook, OptOutSettings } from '@/types/settings';
import type { MessageTemplate } from '@/types/messaging';
import { doc, setDoc, getDoc, deleteDoc, onFirestoreRefresh } from '@/lib/firebase/firestore';
import { deleteField } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthStore, getEffectiveUid, getWriteUid, readAcceptedInviteTime, writeAcceptedInviteTime } from './auth-store';
import { useAppStore } from './app-store';
import { getIncomingDefaultModule } from '@/config/incoming-default-schema';
import { getStorageItem, setStorageItem } from '@/lib/utils/storage';
import { writeScopedCache } from '@/lib/cache/scoped-cache';
import { normalizeOptOutSettings } from '@/lib/utils/opt-out';

/** Prefix for saved message template keys in Firestore */
const TEMPLATE_PREFIX = 'ua_msg_template_item_';

/** Prefix for cached WhatsApp template keys on the org doc (for server-side reuse) */
const WA_TEMPLATE_CACHE_PREFIX = '_ua_app_cache_tpa_wa_templates_';

// Firestore keys (stored in the personal preferences doc)
const KEYS = {
  FIELD_MAPPINGS: 'ua_field_mappings',
  WORKFLOWS: 'ua_workflows',
  PREFERENCES: 'ua_app_preferences',
  SELECTED_ADMIN: 'ua_selected_admin_user_id',
  SELECTED_ADMIN_EMAIL: 'ua_selected_admin_user_email',
  SELECTED_ADMIN_INVITE_TIME: 'ua_selected_admin_invite_time',
  PINNED_CHATS: 'ua_pinned_chat_ids',
} as const;

/**
 * Max conversations a user can pin. Matches the legacy cap enforced in
 * `firebase-things.js` chatMoreOptionItemOnClick ("you can pin up to three
 * chats!") so pins stay compatible between the old and new app.
 */
export const MAX_PINNED_CHATS = 3;

/** Prefix for individual user mapping keys in Firestore */
const USER_MAPPING_PREFIX = 'ua_incoming_tpa_saas_user_mapping_';

/**
 * Module-level in-flight promise for loadAllSettings.
 * On boot, multiple call sites (use-app-init steps 6d/6b/background sync) can
 * fire loadAllSettings concurrently — they all read the same monolithic
 * personal preferences doc. Sharing one in-flight promise prevents redundant
 * Firestore reads (the Rate Guard previously detected 5+ duplicate getDoc calls
 * in <50ms on voice-enabled apps).
 */
let _inflightLoadAll: Promise<void> | null = null;

/**
 * Preference keys changed locally that a concurrent `loadAllSettings` must
 * not clobber. The background sync (~4s after boot, use-app-init) reads the
 * prefs doc, awaits other work, then set()s — if the user flipped a toggle in
 * between, that set() silently reverted it (seen as "Enter to Send" jumping
 * back on ~5s after being switched off). `updatePreferences` records its
 * keys here; a load re-applies them on top of whatever it read and drops a
 * key once the server doc carries the same value (or the write failed).
 */
let _localPrefOverrides: Partial<AppPreferences> = {};

/**
 * Drop any pending local overrides on sign-out / uid change. Without this,
 * a `loadAllSettings` for the NEW user re-applies the PREVIOUS user's
 * overrides on top of the freshly loaded doc (see the shielding logic
 * above `_localPrefOverrides` is used for) — permanently writing the old
 * account's toggle values into the new account's preferences doc.
 */
export function clearLocalPrefOverrides() {
  _localPrefOverrides = {};
}

interface SettingsStore {
  fieldMappings: FieldMapping[];
  webhooks: WebhookConfigSetting[];
  workflows: WorkflowRule[];
  preferences: AppPreferences;
  /** User mapping: TPA user email → SaaS user email */
  userMappings: Record<string, string>;
  incomingCapture: IncomingCaptureSettings;
  incomingCaptureLoading: boolean;
  /** Number of senders linked to the messaging service (set by incoming sync page) */
  incomingLinkedCount: number;

  // SaaS-side incoming sync (for standalone TPA → SaaS recording)
  incomingSyncSaas: IncomingSyncSaasConfig;
  incomingSyncSaasLoading: boolean;

  // Event incoming capture settings (for Calendly/Acuity)
  eventIncomingCapture: EventIncomingCaptureSettings;
  eventIncomingCaptureLoading: boolean;

  /**
   * Org-level: member emails hidden from event agent pickers (Calendly,
   * Acuity, etc). Admin-managed; applies to all users in the org. Independent
   * of incoming-capture settings — this only controls picker visibility.
   * Persisted to the org doc under `ua_hidden_event_members`.
   */
  hiddenEventMembers: string[];

  /** Org-level: voice calling feature toggle (admin-only write) */
  enableVoiceCalling: boolean;
  /** Org-level: KAN-155 customer opt-out settings (`ua_optout_settings`, admin-only write).
   *  Read off the boot-time org doc — no extra Firestore read. */
  optOutSettings: OptOutSettings;
  /** Org-level: Pinnacle WhatsApp API version ('1' or '2'). Read from ua_pinnacle_whatsapp_api_version. */
  pinnacleWaApiVersion: string;
  /** Saved message templates extracted from personal + org docs (no extra Firestore reads) */
  savedTemplates: MessageTemplate[];
  savedTemplatesLoaded: boolean;
  /** Org-level: ids of WhatsApp templates already cached on the org doc
   *  (keys `_ua_app_cache_tpa_wa_templates_<id>`). Seeded from the boot-time
   *  org doc read so cache writes can skip already-stored templates without
   *  re-reading Firestore. */
  cachedWaTemplateIds: Set<string>;
  /** WooCommerce notification rules (org-level) */
  notificationRules: WooCommerceNotificationRule[];
  notificationRulesLoaded: boolean;
  /** Outbound forwarding webhooks — keyed by id. Loaded from the
   *  `FORWARDING_WEBHOOKS_LIST` field on the personal preferences doc
   *  (legacy schema, see `firebase-things.js:21112-21121`). */
  forwardingWebhooks: Record<string, ForwardingWebhook>;

  loading: boolean;
  error: string | null;
  settingsLoaded: boolean;
  /** Google Drive: persisted root folder ID (from ua_selected_root_folderId) */
  gdDriveRootFolderId: string | null;
  /** Google Drive: thumbnail visibility (from LIST_CONTAINER_VIEW_DATA) */
  gdThumbnailVisibility: 'show' | 'hide';
  /** Twilio Event Streams sink SID (from ua_twilio_sink_id) */
  uaTwilioSinkId: string | null;
  /** Twilio Event Streams subscription SID (from ua_twilio_subscription_id) */
  uaTwilioSubscriptionId: string | null;
  /**
   * Conversation IDs the user has pinned to the top of the inbox. Personal
   * (per-user) setting persisted to `ua_pinned_chat_ids` — the same key the
   * legacy app uses, so pins carry across both apps. Capped at
   * `MAX_PINNED_CHATS`.
   */
  pinnedConversationIds: string[];

  // Load all settings from Firestore
  loadAllSettings(): Promise<void>;

  /** Replace the pinned-conversation list in store + Firestore. */
  setPinnedConversationIds(ids: string[]): Promise<void>;

  /** Cache prepared WhatsApp templates onto the org doc (for server-side reuse),
   *  writing only templates not already in `cachedWaTemplateIds`, in a single
   *  merge call. No extra Firestore read. */
  cacheWaTemplatesToOrg(templates: MessageTemplate[]): Promise<void>;

  setFieldMappings(mappings: FieldMapping[]): void;
  addFieldMapping(mapping: FieldMapping): void;
  updateFieldMapping(id: string, updates: Partial<FieldMapping>): void;
  removeFieldMapping(id: string): void;

  setWebhooks(webhooks: WebhookConfigSetting[]): void;
  addWebhook(webhook: WebhookConfigSetting): void;
  updateWebhook(id: string, updates: Partial<WebhookConfigSetting>): void;
  removeWebhook(id: string): void;

  setWorkflows(workflows: WorkflowRule[]): void;
  addWorkflow(workflow: WorkflowRule): void;
  updateWorkflow(id: string, updates: Partial<WorkflowRule>): void;
  removeWorkflow(id: string): void;

  updatePreferences(updates: Partial<AppPreferences>): void;

  // User mapping actions
  setUserMapping(tpaEmail: string, saasEmail: string): void;
  removeUserMapping(tpaEmail: string): void;
  /** Update org-level voice calling toggle (writes to org doc) */
  updateEnableVoiceCalling(enabled: boolean): void;
  /** Save org-level opt-out settings (writes to org doc). Resolves false on failure. */
  updateOptOutSettings(next: OptOutSettings): Promise<boolean>;
  /** Update org-level Pinnacle WhatsApp API version (writes to org doc) */
  updatePinnacleWaApiVersion(version: string): void;

  // Incoming capture settings (messaging apps)
  loadIncomingCaptureSettings(): Promise<void>;
  updateIncomingCapture(updates: Partial<IncomingCaptureSettings>): Promise<void>;
  setIncomingLinkedCount(count: number): void;

  // SaaS-side incoming sync (standalone TPA → SaaS recording)
  loadIncomingSyncSaas(): Promise<void>;
  updateIncomingSyncSaas(updates: Partial<IncomingSyncSaasConfig>): Promise<void>;

  // Event incoming capture settings (event apps)
  loadEventIncomingCaptureSettings(): Promise<void>;
  updateEventIncomingCapture(updates: Partial<EventIncomingCaptureSettings>): Promise<void>;
  /** Update org-level hidden-member list for event agent pickers (writes to org doc) */
  updateHiddenEventMembers(emails: string[]): Promise<void>;

  /** Update saved templates in store (after save/delete without re-reading Firestore) */
  setSavedTemplates(templates: MessageTemplate[]): void;

  /** WooCommerce notification rules CRUD (persisted to org doc) */
  saveNotificationRules(rules: WooCommerceNotificationRule[]): void;

  /** Forwarding webhooks CRUD — single field write per call (merge:
   *  true) so concurrent edits don't clobber siblings. Up to 5 entries
   *  per user (legacy cap). */
  upsertForwardingWebhook(webhook: ForwardingWebhook): Promise<void>;
  removeForwardingWebhook(id: string): Promise<void>;
  setForwardingWebhookState(id: string, state: boolean): Promise<void>;

  /** Update Twilio sink ID in store (Firestore persistence handled by CF) */
  setTwilioSinkId(id: string | null): void;
  /** Update Twilio subscription ID in store */
  setTwilioSubscriptionId(id: string | null): void;

  setLoading(loading: boolean): void;
  setError(error: string | null): void;
}

const defaultPreferences: AppPreferences = {
  defaultChannel: 'SMS',
  autoSaveRecipients: true,
  showDeliveryReports: true,
  enableNotifications: true,
  language: 'en',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const defaultIncomingCapture: IncomingCaptureSettings = {
  enabled: false,
  smsEnabled: true,
  whatsappEnabled: true,
  autoCreateContacts: true,
};

const defaultIncomingSyncSaas: IncomingSyncSaasConfig = {
  enabled: false,
  // Empty until resolved from the server incoming-schema default — never
  // assume "Leads" (wrong for Freshdesk/Monday/etc.).
  targetModule: '',
  autoCreateContacts: true,
  uploadAttachmentToModule: true,
};

const defaultEventIncomingCapture: EventIncomingCaptureSettings = {
  enabled: false,
  ignoredEventTypes: [],
  ignoreNewEventTypes: false,
  ignoreNewCustomers: false,
};

/**
 * Get the Firestore document path for user preferences.
 * Path: uaAppPreferences/{saasCode}_orgs/{orgId}/{extensionName}/personal/{uid}
 *
 * @param forRead — when true, uses getEffectiveUid() (impersonated user OR
 *                   shared-admin if active) so reads reflect the target user.
 *                   When false (write), uses getWriteUid() which respects
 *                   impersonation only — shared-admin users write to their
 *                   own prefs, not the admin's.
 */
function getPreferencesDocPath(forRead = false): string | null {
  const uid = forRead ? getEffectiveUid() : getWriteUid();
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;

  if (!uid || !extensionName || !orgId || !saasCode) {
    return null;
  }

  return `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/personal/${uid}`;
}

/** Org-level (root) preferences doc — settings shared across users */
function getOrgDocPath(): string | null {
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;

  if (!extensionName || !orgId || !saasCode) {
    return null;
  }

  return `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}`;
}

/**
 * Persist a single key to the preferences Firestore doc (merge).
 * Exported so other modules (e.g. use-google-drive) can write top-level keys.
 */
export async function persistToFirestore(key: string, value: unknown): Promise<void> {
  const docPath = getPreferencesDocPath();
  if (!docPath) return;

  try {
    const docRef = doc(db, docPath);
    await setDoc(docRef, { [key]: value }, { merge: true });
  } catch (error) {
    console.error(`[Settings] Failed to persist ${key}:`, error);
    throw error;
  }
}

/**
 * Add a SaaS/CRM user to the org-level member cache so it shows up in the
 * org members list (e.g. dashboard). Stored on the org doc under
 * `_ua_app_cache_saas_my_org_members_<id>`. Mirrors legacy
 * addToOrgCacheIfNotExists('saas_my_org_members_' + id, user, 'email'):
 * skips the write when an entry with the same email already exists so we
 * don't clobber a richer cached entry (photo/isAdmin).
 */
export async function addSaasUserToOrgMembersCache(user: {
  id: string;
  name: string;
  email: string;
}): Promise<void> {
  const orgPath = getOrgDocPath();
  if (!orgPath || !user?.id || !user?.email) return;

  const key = `_ua_app_cache_saas_my_org_members_${user.id}`;
  try {
    const docRef = doc(db, orgPath);
    const snap = await getDoc(docRef);
    const existing = snap.exists()
      ? (snap.data()?.[key] as { email?: string } | undefined)
      : undefined;
    if (existing && existing.email === user.email) return; // already cached

    await setDoc(
      docRef,
      { [key]: { id: user.id, name: user.name, email: user.email } },
      { merge: true }
    );
  } catch (error) {
    console.error('[Settings] Failed to cache SaaS org member:', error);
  }
}

/** Extract saved message templates from a Firestore doc's data */
function extractTemplatesFromDoc(
  data: Record<string, unknown>,
  source: 'org' | 'personal'
): MessageTemplate[] {
  const templates: MessageTemplate[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (!key.startsWith(TEMPLATE_PREFIX)) continue;
    const tmpl = value as { title?: string; message?: string; visibility?: string };
    if (!tmpl?.title || !tmpl?.message) continue;
    const name = key.replace(TEMPLATE_PREFIX, '');
    const vars = tmpl.message.match(/\{\{(\w+)\}\}/g);
    templates.push({
      id: key,
      name: tmpl.title || name,
      body: tmpl.message,
      channel: 'SMS',
      category: source === 'personal' ? 'Personal' : 'Shared',
      status: 'approved',
      variables: vars ? Array.from(new Set(vars.map((m: string) => m.replace(/\{\{|\}\}/g, '')))) : [],
    });
  }
  return templates;
}

/**
 * Serialize dates in objects for Firestore storage.
 * Note: JSON.stringify drops keys with `undefined` values. When persisting
 * preferences that were explicitly set to `undefined` (e.g. clearing a default
 * sender), those keys must be preserved as `null` so Firestore overwrites
 * the old value instead of keeping it (due to `merge: true`).
 */
function serializeForStorage<T>(data: T): T {
  return JSON.parse(JSON.stringify(data));
}

/**
 * Strip `null` values from an object so they become `undefined` in JS.
 * Used when loading preferences from Firestore where we store `null`
 * to represent "cleared/unset" values.
 */
function stripNulls<T extends Record<string, unknown>>(obj: T): T {
  const result = { ...obj };
  for (const key of Object.keys(result)) {
    if (result[key] === null) {
      delete result[key];
    }
  }
  return result;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  fieldMappings: [],
  webhooks: [],
  workflows: [],
  // Seed preferences from localStorage cache for instant availability on reload.
  // Firestore data will overwrite this when loadAllSettings() completes.
  preferences: {
    ...defaultPreferences,
    ...stripNulls(getStorageItem<Partial<AppPreferences>>('app_preferences', {})),
  },
  // Webhooks are deliberately NOT cached to localStorage — the list is
  // small enough that the Firestore round-trip on settings load is fine,
  // and avoiding the cache means we never serve stale entries if a webhook
  // was deleted on another device. Initial empty + a loading hint in the
  // UI covers the brief ~4s window between refresh and background sync.
  forwardingWebhooks: {},
  userMappings: {},
  incomingCapture: defaultIncomingCapture,
  incomingCaptureLoading: false,
  incomingLinkedCount: -1, // -1 = not yet fetched
  incomingSyncSaas: defaultIncomingSyncSaas,
  incomingSyncSaasLoading: false,
  eventIncomingCapture: defaultEventIncomingCapture,
  eventIncomingCaptureLoading: false,
  hiddenEventMembers: [],
  enableVoiceCalling: false,
  optOutSettings: normalizeOptOutSettings(null),
  pinnacleWaApiVersion: '1',
  savedTemplates: [],
  savedTemplatesLoaded: false,
  cachedWaTemplateIds: new Set<string>(),
  notificationRules: [],
  notificationRulesLoaded: false,
  loading: false,
  error: null,
  settingsLoaded: false,
  gdDriveRootFolderId: null,
  gdThumbnailVisibility: 'show',
  uaTwilioSinkId: null,
  uaTwilioSubscriptionId: null,
  pinnedConversationIds: [],

  /**
   * Load all settings from Firestore in a single read.
   *
   * De-duped: concurrent invocations share one in-flight promise so the
   * monolithic personal preferences doc is only fetched once per boot cycle.
   */
  loadAllSettings: async () => {
    if (_inflightLoadAll) {
      console.log('[Settings] loadAllSettings de-duped — returning in-flight promise');
      return _inflightLoadAll;
    }
    _inflightLoadAll = (async () => {
    // Team permissions: always attempt to load regardless of whether
    // the preferences doc path is available. Teams use their own shared
    // doc path and should sync independently of personal settings.
    const teamPromise = import('./team-store').then(({ useTeamStore }) =>
      useTeamStore.getState().loadTeams()
    ).catch((err) => {
      console.warn('[Settings] Team permissions load failed:', err);
    });

    const docPath = getPreferencesDocPath();
    if (!docPath) {
      console.warn('[Settings] Cannot load: missing user/app context');
      // Still mark as loaded so components gated on settingsLoaded (e.g.
      // ConversationsView) aren't blocked forever. Defaults will be used.
      set({ settingsLoaded: true, savedTemplatesLoaded: true, notificationRulesLoaded: true });
      await teamPromise;
      return;
    }

    const uid = useAuthStore.getState().user?.uid;
    if (!uid) {
      set({ settingsLoaded: true, savedTemplatesLoaded: true, notificationRulesLoaded: true });
      await teamPromise;
      return;
    }

    set({ loading: true, error: null });

    try {
      const orgDocPath = getOrgDocPath();
      console.log(`[Settings] Loading — personal: ${docPath}, org: ${orgDocPath || '(null, skipped)'}`);

      // Read personal doc + org doc + team permissions in parallel.
      // Org doc may fail with permission errors for some users — catch gracefully.
      const [snapshot, orgSnapshot] = await Promise.all([
        getDoc(doc(db, docPath)),
        orgDocPath
          ? getDoc(doc(db, orgDocPath)).catch((err) => {
            console.warn(`[Settings] Org doc read failed (${orgDocPath}):`, err?.message || err);
            return null;
          })
          : Promise.resolve(null),
        teamPromise,
      ]);
      console.log(`[Settings] Org doc result:`, orgSnapshot ? (orgSnapshot.exists() ? 'exists' : 'empty') : 'null/skipped');

      // Extract org-level settings (defaults to false if org doc unreadable)
      const orgData = orgSnapshot?.exists() ? orgSnapshot.data() : {};
      const enableVoiceCalling = !!orgData?.ua_enable_voice_calling;
      const optOutSettings = normalizeOptOutSettings(orgData?.ua_optout_settings);
      // Coerce to a trimmed string: Firestore may hold the value as a number (2)
      // or with stray whitespace, and useV3Api compares strictly against '2'.
      const pinnacleWaApiVersion = String(orgData?.ua_pinnacle_whatsapp_api_version ?? '').trim() || '1';
      // Only log for orgs that actually have the Pinnacle version set, so other
      // apps' loads stay quiet.
      if (orgData?.ua_pinnacle_whatsapp_api_version !== undefined) {
        console.log('[Settings] pinnacleWaApiVersion resolved:', JSON.stringify(pinnacleWaApiVersion), '(raw:', JSON.stringify(orgData?.ua_pinnacle_whatsapp_api_version), ')');
      }
      // Org-level hidden event members — needs to be available to every user
      // (not just admins who open the settings page) so the agent picker
      // filters correctly on first load. Cached to localStorage scoped by
      // extension so subsequent loads have it before the Firestore sync
      // completes (the bootstrap flips settingsLoaded based on the
      // app_preferences cache, which would otherwise expose unfiltered chips).
      const hiddenEventMembers = Array.isArray(orgData?.ua_hidden_event_members)
        ? (orgData.ua_hidden_event_members as string[])
        : [];
      try {
        const extName = useAppStore.getState().extensionName;
        if (extName) {
          localStorage.setItem(
            `ua_cache_hidden_event_members_${extName}`,
            JSON.stringify(hiddenEventMembers)
          );
        }
      } catch { /* */ }

      // Extract WooCommerce notification rules from org doc
      const notificationRules: WooCommerceNotificationRule[] =
        Array.isArray(orgData?.ua_woocommerce_notification_rules)
          ? orgData.ua_woocommerce_notification_rules
          : [];

      // Extract saved templates from both docs (avoids separate Firestore reads)
      const orgTemplates = orgData ? extractTemplatesFromDoc(orgData as Record<string, unknown>, 'org') : [];

      // Retain ids of already-cached WA templates so cacheWaTemplatesToOrg can
      // do its "if not exists" check without re-reading the org doc.
      const cachedWaTemplateIds = new Set<string>();
      for (const key of Object.keys(orgData ?? {})) {
        if (key.startsWith(WA_TEMPLATE_CACHE_PREFIX)) {
          cachedWaTemplateIds.add(key.slice(WA_TEMPLATE_CACHE_PREFIX.length));
        }
      }

      const personalTemplatesArr: MessageTemplate[] = [];

      if (snapshot.exists()) {
        const data = snapshot.data();
        personalTemplatesArr.push(...extractTemplatesFromDoc(data, 'personal'));

        // ── Trial period: extract start time and pass to trial-store ──
        // Piggybacks on this existing Firestore read — no extra call needed.
        const { useTrialStore } = await import('./trial-store');
        const trialStartTime = data['ua_app_readonly_trial_start_time'];
        useTrialStore.getState().setTrialFromSettings(
          typeof trialStartTime === 'number' ? trialStartTime : null
        );

        // Field mappings
        const fieldMappings: FieldMapping[] = data[KEYS.FIELD_MAPPINGS] || [];

        // Workflows (rehydrate dates)
        const rawWorkflows: WorkflowRule[] = data[KEYS.WORKFLOWS] || [];
        const workflows = rawWorkflows.map((w) => ({
          ...w,
          createdAt: w.createdAt ? new Date(w.createdAt) : new Date(),
        }));

        // Preferences — strip nulls so cleared values become undefined in JS
        const storedPrefs = data[KEYS.PREFERENCES];
        const loadedPrefs: AppPreferences = storedPrefs
          ? { ...defaultPreferences, ...stripNulls(storedPrefs) }
          : defaultPreferences;
        // Keep local changes made while this load was in flight (see
        // _localPrefOverrides); forget the ones the server has caught up with.
        for (const key of Object.keys(_localPrefOverrides) as (keyof AppPreferences)[]) {
          if (JSON.stringify(loadedPrefs[key]) === JSON.stringify(_localPrefOverrides[key])) {
            delete _localPrefOverrides[key];
          }
        }
        const preferences: AppPreferences = { ...loadedPrefs, ..._localPrefOverrides };

        // Forwarding webhooks — top-level field (NOT nested under
        // ua_app_preferences) so legacy + the CF that fans events out
        // both keep their existing shape.
        const rawForwardingWebhooks = data['FORWARDING_WEBHOOKS_LIST'];
        const forwardingWebhooks: Record<string, ForwardingWebhook> =
          rawForwardingWebhooks && typeof rawForwardingWebhooks === 'object'
            ? (rawForwardingWebhooks as Record<string, ForwardingWebhook>)
            : {};

        // User mappings — scan for keys with the mapping prefix
        const userMappings: Record<string, string> = {};
        for (const [key, value] of Object.entries(data)) {
          if (key.startsWith(USER_MAPPING_PREFIX) && typeof value === 'string') {
            const tpaUserId = key.substring(USER_MAPPING_PREFIX.length);
            userMappings[tpaUserId] = value;
          }
        }

        // Incoming capture
        const enabled = data[`ua_incoming_enable_capture_${uid}`] === true;
        const smsEnabled = data[`ua_incoming_channel_sms_enabled_${uid}`] !== false;
        const whatsappEnabled = data[`ua_incoming_channel_whatsapp_enabled_${uid}`] !== false;
        const autoCreateContacts = data[`ua_incoming_auto_create_contacts_${uid}`] !== false;

        // Google Drive specific top-level keys
        const gdDriveRootFolderId = (data['ua_selected_root_folderId'] as string | null) || null;
        // Twilio Event Streams sink + subscription IDs
        const uaTwilioSinkId = (data['ua_twilio_sink_id'] as string | null) || null;
        const uaTwilioSubscriptionId = (data['ua_twilio_subscription_id'] as string | null) || null;
        const listViewData = (data['LIST_CONTAINER_VIEW_DATA'] as Record<string, string>) || {};
        const gdThumbnailVisibility: 'show' | 'hide' = listViewData['thumbnail-visibility'] === 'hide' ? 'hide' : 'show';
        // Pinned inbox conversations — legacy stores a plain array of conv ids.
        // Guard the element type: a malformed doc must not poison the list.
        const rawPinned = data[KEYS.PINNED_CHATS];
        const pinnedConversationIds = Array.isArray(rawPinned)
          ? rawPinned.filter((id): id is string => typeof id === 'string' && id.length > 0)
          : [];
        // Merge templates: personal overrides org by key
        const templateMap = new Map<string, MessageTemplate>();
        for (const t of orgTemplates) templateMap.set(t.id, t);
        for (const t of personalTemplatesArr) templateMap.set(t.id, t);

        set({
          fieldMappings,
          workflows,
          preferences,
          userMappings,
          enableVoiceCalling,
          optOutSettings,
          pinnacleWaApiVersion,
          gdDriveRootFolderId,
          gdThumbnailVisibility,
          uaTwilioSinkId,
          uaTwilioSubscriptionId,
          pinnedConversationIds,
          savedTemplates: Array.from(templateMap.values()),
          savedTemplatesLoaded: true,
          cachedWaTemplateIds,
          notificationRules,
          notificationRulesLoaded: true,
          forwardingWebhooks,
          incomingCapture: {
            ...get().incomingCapture,
            enabled,
            smsEnabled,
            whatsappEnabled,
            autoCreateContacts,
          },
          hiddenEventMembers,
          loading: false,
          settingsLoaded: true,
        });

        // Cache preferences + trial start time to localStorage for instant availability on next page load.
        setStorageItem('app_preferences', preferences);
        // Empty value clears the scoped key rather than caching '' — an
        // empty string used to persist and read back as a bogus cache hit.
        writeScopedCache(
          'ua_cache_trial_start',
          typeof trialStartTime === 'number' ? trialStartTime.toString() : null,
        );

        // Restore selected admin from personal settings (no extra Firestore call)
        const savedAdminId = data[KEYS.SELECTED_ADMIN] as string | undefined;
        const savedAdminEmail = data[KEYS.SELECTED_ADMIN_EMAIL] as string | undefined;
        const savedInviteTime = data[KEYS.SELECTED_ADMIN_INVITE_TIME] as number | undefined;
        if (savedAdminId) {
          const authState = useAuthStore.getState();
          // Only set if not already set (e.g. from localStorage optimistic restore)
          if (!authState.selectedAdminUserId) {
            authState.setSelectedAdminSilent(savedAdminId, savedAdminEmail ?? null);
            console.log('[Settings] Restored selectedAdmin from personal settings:', savedAdminId);
          }
          // Seed the localStorage mirror of "which invite was accepted" when
          // this device doesn't have one yet (fresh browser, cleared cache).
          // Never overwrite a local value — after an accept, local is the
          // freshest. Without the seed the removal check can only see a
          // deleted invite, not one re-issued after a removal.
          if (typeof savedInviteTime === 'number' && readAcceptedInviteTime() == null) {
            writeAcceptedInviteTime(savedInviteTime);
            // The membership check may already have run without it.
            void authState.verifySelectedAdminMembership();
          }
        }

        console.log('[Settings] Loaded all settings from Firestore');
      } else {
        // Doc doesn't exist yet — still mark trial as loaded (no start time)
        const { useTrialStore } = await import('./trial-store');
        useTrialStore.getState().setTrialFromSettings(null);

        set({
          enableVoiceCalling,
          optOutSettings,
          pinnacleWaApiVersion,
          savedTemplates: orgTemplates,
          savedTemplatesLoaded: true,
          cachedWaTemplateIds,
          notificationRules,
          notificationRulesLoaded: true,
          hiddenEventMembers,
          loading: false,
          settingsLoaded: true,
        });
      }
    } catch (error) {
      console.error('[Settings] Failed to load settings:', error);
      // Mark trial as loaded even on error so the UI isn't stuck
      import('./trial-store').then(({ useTrialStore }) => {
        useTrialStore.getState().setTrialFromSettings(null);
      }).catch(() => { /* ignore */ });

      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load settings',
        settingsLoaded: true,
        savedTemplatesLoaded: true,
        notificationRulesLoaded: true,
      });
    }
    })().finally(() => { _inflightLoadAll = null; });
    return _inflightLoadAll;
  },

  cacheWaTemplatesToOrg: async (templates) => {
    if (!templates.length) return;
    const orgPath = getOrgDocPath();
    if (!orgPath) return;

    // "If not exists" check against the in-memory ids seeded by loadAllSettings
    // (no extra Firestore read). Only missing templates are written, all in one
    // merge call. Mirrors legacy cacheWATemplateIfNotPresentInOrgSetting.
    const cached = get().cachedWaTemplateIds;
    const updates: Record<string, unknown> = {};
    const newIds: string[] = [];
    for (const template of templates) {
      if (!template.id || cached.has(template.id)) continue;
      // Firestore rejects nested arrays. The v3 parser's `components` carries
      // example.body_text (string[][]) which the server resolver never reads
      // from the cache (it uses name/body/language/headerMedia, and the live
      // components from the webhook). Drop it so the write doesn't fail.
      const { components: _omitComponents, ...cacheable } = template;
      void _omitComponents;
      updates[`${WA_TEMPLATE_CACHE_PREFIX}${template.id}`] = cacheable;
      newIds.push(template.id);
    }
    if (newIds.length === 0) return;

    try {
      await setDoc(doc(db, orgPath), updates, { merge: true });
      set({ cachedWaTemplateIds: new Set([...cached, ...newIds]) });
      console.log(`[Settings] Cached ${newIds.length} WhatsApp template(s) to org settings`);
    } catch (err) {
      console.warn('[Settings] Error caching WhatsApp templates to org settings:', err);
    }
  },

  // ── Field Mappings (persisted) ──────────────────────────────

  setFieldMappings: (mappings) => {
    set({ fieldMappings: mappings });
    persistToFirestore(KEYS.FIELD_MAPPINGS, serializeForStorage(mappings)).catch(() => { });
  },

  addFieldMapping: (mapping) => {
    const updated = [...get().fieldMappings, mapping];
    set({ fieldMappings: updated });
    persistToFirestore(KEYS.FIELD_MAPPINGS, serializeForStorage(updated)).catch(() => { });
  },

  updateFieldMapping: (id, updates) => {
    const updated = get().fieldMappings.map((m) =>
      m.id === id ? { ...m, ...updates } : m
    );
    set({ fieldMappings: updated });
    persistToFirestore(KEYS.FIELD_MAPPINGS, serializeForStorage(updated)).catch(() => { });
  },

  removeFieldMapping: (id) => {
    const updated = get().fieldMappings.filter((m) => m.id !== id);
    set({ fieldMappings: updated });
    persistToFirestore(KEYS.FIELD_MAPPINGS, serializeForStorage(updated)).catch(() => { });
  },

  // ── Webhooks (local only — webhook config is managed by TPA modules) ──

  setWebhooks: (webhooks) => set({ webhooks }),
  addWebhook: (webhook) =>
    set({ webhooks: [...get().webhooks, webhook] }),
  updateWebhook: (id, updates) =>
    set({
      webhooks: get().webhooks.map((w) =>
        w.id === id ? { ...w, ...updates } : w
      ),
    }),
  removeWebhook: (id) =>
    set({ webhooks: get().webhooks.filter((w) => w.id !== id) }),

  // ── Workflows (persisted) ───────────────────────────────────

  setWorkflows: (workflows) => {
    set({ workflows });
    persistToFirestore(KEYS.WORKFLOWS, serializeForStorage(workflows)).catch(() => { });
  },

  addWorkflow: (workflow) => {
    const updated = [...get().workflows, workflow];
    set({ workflows: updated });
    persistToFirestore(KEYS.WORKFLOWS, serializeForStorage(updated)).catch(() => { });
  },

  updateWorkflow: (id, updates) => {
    const updated = get().workflows.map((w) =>
      w.id === id ? { ...w, ...updates } : w
    );
    set({ workflows: updated });
    persistToFirestore(KEYS.WORKFLOWS, serializeForStorage(updated)).catch(() => { });
  },

  removeWorkflow: (id) => {
    const updated = get().workflows.filter((w) => w.id !== id);
    set({ workflows: updated });
    persistToFirestore(KEYS.WORKFLOWS, serializeForStorage(updated)).catch(() => { });
  },

  // ── User Mappings (persisted) ───────────────────────────────

  setUserMapping: (tpaEmail, saasEmail) => {
    const updated = { ...get().userMappings, [tpaEmail]: saasEmail };
    set({ userMappings: updated });
    persistToFirestore(`${USER_MAPPING_PREFIX}${tpaEmail}`, saasEmail).catch(() => { });
  },

  removeUserMapping: (tpaEmail) => {
    const updated = { ...get().userMappings };
    delete updated[tpaEmail];
    set({ userMappings: updated });
    persistToFirestore(`${USER_MAPPING_PREFIX}${tpaEmail}`, '').catch(() => { });
  },

  // ── Pinned conversations (persisted) ────────────────────────

  /**
   * Optimistic: store updates first so the inbox reorders instantly, then the
   * Firestore write lands. On failure the previous list is restored so the UI
   * never claims a pin that was not saved.
   */
  setPinnedConversationIds: async (ids) => {
    const previous = get().pinnedConversationIds;
    set({ pinnedConversationIds: ids });
    try {
      await persistToFirestore(KEYS.PINNED_CHATS, ids);
    } catch {
      set({ pinnedConversationIds: previous });
      toast.error('Failed to save pinned chat');
    }
  },

  // ── Preferences (persisted) ─────────────────────────────────

  updatePreferences: (updates) => {
    const updated = { ...get().preferences, ...updates };
    set({ preferences: updated });

    // Cache to localStorage immediately for instant availability on next reload
    setStorageItem('app_preferences', updated);

    // JSON.stringify drops undefined keys, but we need those to overwrite
    // existing Firestore values (with merge: true). Convert undefined → null.
    const forStorage = serializeForStorage(updated);
    for (const key of Object.keys(updates)) {
      if ((updates as Record<string, unknown>)[key] === undefined) {
        (forStorage as Record<string, unknown>)[key] = null;
      }
    }

    // Shield these keys from a concurrent/background loadAllSettings until
    // the server doc reflects them (a later load drops them itself).
    _localPrefOverrides = { ..._localPrefOverrides, ...updates };

    persistToFirestore(KEYS.PREFERENCES, forStorage)
      .then(() => toast.success('Setting saved'))
      .catch(() => {
        // Write failed — don't keep pretending; the next load shows server truth.
        for (const key of Object.keys(updates) as (keyof AppPreferences)[]) {
          delete _localPrefOverrides[key];
        }
        toast.error('Failed to save setting');
      });
  },

  // ── Org-level voice calling toggle ─────────────────────────

  updateEnableVoiceCalling: (enabled) => {
    set({ enableVoiceCalling: enabled });

    const orgPath = getOrgDocPath();
    if (!orgPath) {
      toast.error('Failed to save setting');
      return;
    }

    const docRef = doc(db, orgPath);
    setDoc(docRef, { ua_enable_voice_calling: enabled }, { merge: true })
      .then(() => toast.success(enabled ? 'Voice calling enabled' : 'Voice calling disabled'))
      .catch(() => {
        set({ enableVoiceCalling: !enabled }); // revert on failure
        toast.error('Failed to save setting');
      });
  },

  // ── Org-level customer opt-out (KAN-155) ────────────────────
  // Whole object on the org doc: the server reads it on incoming messages and
  // workflow sends, and every user of the org sees the same inbox lock.
  updateOptOutSettings: async (next) => {
    const orgPath = getOrgDocPath();
    if (!orgPath) {
      toast.error('Failed to save setting');
      return false;
    }
    const prev = get().optOutSettings;
    set({ optOutSettings: next });
    try {
      await setDoc(doc(db, orgPath), { ua_optout_settings: next }, { merge: true });
      toast.success('Setting saved'); // same confirmation as every other auto-saved setting
      return true;
    } catch (err) {
      console.warn('[Settings] Failed to save opt-out settings:', (err as Error)?.message || err);
      set({ optOutSettings: prev }); // revert on failure
      toast.error('Failed to save setting');
      return false;
    }
  },

  // ── Org-level Pinnacle WhatsApp API version ─────────────��────

  updatePinnacleWaApiVersion: (version) => {
    const normalized = String(version ?? '').trim() || '1';
    set({ pinnacleWaApiVersion: normalized });

    const orgPath = getOrgDocPath();
    if (!orgPath) {
      // orgId/saasCode not resolved yet — the value lives in memory but can't be
      // persisted to org settings. Surface it instead of silently dropping it.
      console.warn('[Settings] pinnacleWaApiVersion not persisted — org doc path unavailable (orgId/appCodeSAAS missing)');
      return;
    }

    const docRef = doc(db, orgPath);
    setDoc(docRef, { ua_pinnacle_whatsapp_api_version: normalized }, { merge: true })
      .then(() => console.log('[Settings] Persisted pinnacleWaApiVersion=' + JSON.stringify(normalized) + ' → ' + orgPath))
      .catch((err) => {
        // Keep the in-memory selection; a transient write failure must NOT
        // silently downgrade the org to v1.
        console.warn('[Settings] Failed to persist pinnacleWaApiVersion:', err?.message || err);
      });
  },

  // ── Incoming Capture (persisted with original key format) ───

  loadIncomingCaptureSettings: async () => {
    const personalPath = getPreferencesDocPath(true);
    const orgPath = getOrgDocPath();
    if (!personalPath) {
      console.warn('[Settings] Cannot load incoming capture: missing user/app context');
      return;
    }

    const uid = getEffectiveUid();
    if (!uid) return;

    set({ incomingCaptureLoading: true });

    try {
      // Read both org-level and personal docs, merge (personal overrides org)
      const personalSnap = await getDoc(doc(db, personalPath));
      const orgSnap = orgPath
        ? await getDoc(doc(db, orgPath)).catch((err) => {
          console.warn(`[Settings] Incoming capture org doc read failed (${orgPath}):`, err?.message || err);
          return null;
        })
        : null;

      const orgData = orgSnap?.exists() ? orgSnap.data() : {};
      const personalData = personalSnap.exists() ? personalSnap.data() : {};
      const data = { ...orgData, ...personalData };

      if (personalSnap.exists() || orgSnap?.exists()) {
        const enabled = !!data[`ua_incoming_enable_capture_${uid}`];
        const smsEnabled = data[`ua_incoming_channel_sms_enabled_${uid}`] !== false;
        const whatsappEnabled = data[`ua_incoming_channel_whatsapp_enabled_${uid}`] !== false;
        const autoCreateContacts = data[`ua_incoming_auto_create_contacts_${uid}`] !== false;

        set({
          incomingCapture: {
            enabled,
            smsEnabled,
            whatsappEnabled,
            autoCreateContacts,
            serviceSid: undefined,
            // Keep undefined when the server hasn't stored a module — the UI
            // resolves the real default from the server-side incoming schema
            // (getIncomingDefaultModule). Avoids a hardcoded "Leads" that is
            // wrong for Freshdesk (Tickets), Monday (boards), etc.
            targetModule: data['ua_incoming_saas_target_module'] || undefined,
            uploadAttachmentToModule: data['upload_attachment_to_module_record'] !== false,
            enableSignalNotifications: !!data['ua_app_setting_incoming_signal_notifi_enable_for_crm'],
            phonePatternNoCountryCode: !!data['ua_incoming_number_fetch_without_country_code'],
            phonePatternStartsWith0: !!data['ua_incoming_number_fetch_starts_with_0'],
            phonePatternStartsWithPlus: !!data['ua_incoming_number_fetch_starts_with_plus'],
            ignoreNewCustomers: !!data['ua_app_setting_incoming_no_create_new_entity'],
            incomingUnreadFieldMap: data['ua_incoming_unread_ref_saas_fields_map'] || undefined,
            defaultDepartmentId: data['ua_incoming_saas_default_department_id'] || undefined,
            ticketReplyCapture: data[`ua_incoming_saas_ticket_reply_enable_capture_${uid}`] !== false,
            fieldsUpdateSync: data[`ua_incoming_saas_fields_update_enable_capture_${uid}`] !== false,
            reopenExistingTicket: !!data['ua_incoming_reopen_existing_ticket'],
            ignoredSenderIds: Array.isArray(data['ua_incoming_tpa_ignored_senders']) ? data['ua_incoming_tpa_ignored_senders'] : [],
            ignoredMessageStatuses: Array.isArray(data['ua_twilio_ignored_message_statuses']) ? data['ua_twilio_ignored_message_statuses'] : [],
            ignoredWhatsappEvents: Array.isArray(data['ua_whatsapp_ignored_message_statuses']) ? data['ua_whatsapp_ignored_message_statuses'].filter((id: string) => id !== 'incoming') : [],
            ignoredWhatsappWebhookEvents: Array.isArray(data['ua_whatsapp_webhook_ignored_message_statuses'])
              ? data['ua_whatsapp_webhook_ignored_message_statuses']
              : Array.isArray(data['ua_whatsapp_ignored_message_statuses']) ? data['ua_whatsapp_ignored_message_statuses'] : [],
            resendSmsOnWhatsappFailure: !!data['ua_twilio_resend_sms_on_whatsapp_failure'],
            whatsappFailureSmsFallbackNumber: data['ua_twilio_wa_failed_sms_fallback_number'] || undefined,
            whatsappFailureSmsSenderMap: data['ua_twilio_wa_failed_sms_sender_map'] || undefined,
            // Monday.com–specific
            historyModule: data['ua_incoming_saas_target_event_module'] || undefined,
            targetPhoneField: data['ua_incoming_saas_target_contact_phone_field'] || undefined,
            targetEmailField: data['ua_incoming_saas_target_contact_email_field'] || undefined,
            customSearchModules: Array.isArray(data['_ua_custome_search_modules_and_fields'])
              ? data['_ua_custome_search_modules_and_fields']
              : undefined,
          },
          incomingCaptureLoading: false,
        });
      } else {
        set({ incomingCaptureLoading: false });
      }
    } catch (error) {
      console.error('[Settings] Failed to load incoming capture settings:', error);
      set({ incomingCaptureLoading: false });
    }
  },

  updateIncomingCapture: async (updates) => {
    const docPath = getPreferencesDocPath();
    if (!docPath) {
      console.warn('[Settings] Cannot save incoming capture: missing user/app context');
      return;
    }

    const uid = getEffectiveUid();
    if (!uid) return;
    const current = get().incomingCapture;
    const newSettings = { ...current, ...updates };

    // Update local state immediately (optimistic)
    set({ incomingCapture: newSettings });

    try {
      const docRef = doc(db, docPath);
      const firestoreUpdates: Record<string, unknown> = {};

      if (updates.enabled !== undefined) {
        firestoreUpdates[`ua_incoming_enable_capture_${uid}`] = updates.enabled;
      }
      if (updates.smsEnabled !== undefined) {
        firestoreUpdates[`ua_incoming_channel_sms_enabled_${uid}`] = updates.smsEnabled;
      }
      if (updates.whatsappEnabled !== undefined) {
        firestoreUpdates[`ua_incoming_channel_whatsapp_enabled_${uid}`] = updates.whatsappEnabled;
      }
      if (updates.autoCreateContacts !== undefined) {
        firestoreUpdates[`ua_incoming_auto_create_contacts_${uid}`] = updates.autoCreateContacts;
      }
      if (updates.targetModule !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_module'] = updates.targetModule;
      }
      if (updates.uploadAttachmentToModule !== undefined) {
        firestoreUpdates['upload_attachment_to_module_record'] = updates.uploadAttachmentToModule;
      }
      if (updates.enableSignalNotifications !== undefined) {
        firestoreUpdates['ua_app_setting_incoming_signal_notifi_enable_for_crm'] = updates.enableSignalNotifications;
      }
      if (updates.phonePatternNoCountryCode !== undefined) {
        firestoreUpdates['ua_incoming_number_fetch_without_country_code'] = updates.phonePatternNoCountryCode;
      }
      if (updates.phonePatternStartsWith0 !== undefined) {
        firestoreUpdates['ua_incoming_number_fetch_starts_with_0'] = updates.phonePatternStartsWith0;
      }
      if (updates.phonePatternStartsWithPlus !== undefined) {
        firestoreUpdates['ua_incoming_number_fetch_starts_with_plus'] = updates.phonePatternStartsWithPlus;
      }
      if (updates.ignoreNewCustomers !== undefined) {
        firestoreUpdates['ua_app_setting_incoming_no_create_new_entity'] = updates.ignoreNewCustomers;
      }
      if (updates.incomingUnreadFieldMap !== undefined) {
        firestoreUpdates['ua_incoming_unread_ref_saas_fields_map'] = updates.incomingUnreadFieldMap;
      }
      if (updates.defaultDepartmentId !== undefined) {
        firestoreUpdates['ua_incoming_saas_default_department_id'] = updates.defaultDepartmentId;
      }
      if (updates.ticketReplyCapture !== undefined) {
        firestoreUpdates[`ua_incoming_saas_ticket_reply_enable_capture_${uid}`] = updates.ticketReplyCapture;
      }
      if (updates.fieldsUpdateSync !== undefined) {
        firestoreUpdates[`ua_incoming_saas_fields_update_enable_capture_${uid}`] = updates.fieldsUpdateSync;
      }
      if (updates.reopenExistingTicket !== undefined) {
        firestoreUpdates['ua_incoming_reopen_existing_ticket'] = updates.reopenExistingTicket;
      }
      if (updates.ignoredSenderIds !== undefined) {
        firestoreUpdates['ua_incoming_tpa_ignored_senders'] = updates.ignoredSenderIds;
      }
      if (updates.ignoredMessageStatuses !== undefined) {
        firestoreUpdates['ua_twilio_ignored_message_statuses'] = updates.ignoredMessageStatuses;
      }
      if (updates.ignoredWhatsappEvents !== undefined) {
        firestoreUpdates['ua_whatsapp_ignored_message_statuses'] = updates.ignoredWhatsappEvents;
      }
      if (updates.ignoredWhatsappWebhookEvents !== undefined) {
        firestoreUpdates['ua_whatsapp_webhook_ignored_message_statuses'] = updates.ignoredWhatsappWebhookEvents;
      }
      if (updates.resendSmsOnWhatsappFailure !== undefined) {
        firestoreUpdates['ua_twilio_resend_sms_on_whatsapp_failure'] = updates.resendSmsOnWhatsappFailure;
      }
      if (updates.whatsappFailureSmsFallbackNumber !== undefined) {
        firestoreUpdates['ua_twilio_wa_failed_sms_fallback_number'] = updates.whatsappFailureSmsFallbackNumber;
      }
      if (updates.whatsappFailureSmsSenderMap !== undefined) {
        firestoreUpdates['ua_twilio_wa_failed_sms_sender_map'] = updates.whatsappFailureSmsSenderMap;
      }
      // Monday.com–specific
      if (updates.historyModule !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_event_module'] = updates.historyModule;
      }
      if (updates.targetPhoneField !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_contact_phone_field'] = updates.targetPhoneField;
      }
      if (updates.targetEmailField !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_contact_email_field'] = updates.targetEmailField;
      }
      if (updates.customSearchModules !== undefined) {
        firestoreUpdates['_ua_custome_search_modules_and_fields'] = updates.customSearchModules;
      }

      await setDoc(docRef, firestoreUpdates, { merge: true });
    } catch (error) {
      console.error('[Settings] Failed to save incoming capture settings:', error);
      // Revert on error
      set({ incomingCapture: current });
    }
  },

  setIncomingLinkedCount: (count) => set({ incomingLinkedCount: count }),

  // ── SaaS-side Incoming Sync ──────────────────────────────────────
  // Path: ulgebraUsers/{uid}/extensions/{extKey}/incomingSyncSaas/{saasCode}_{orgId}
  // Twilio & WhatsApp use "{tpa}forpage"; other extensions use extensionName.

  loadIncomingSyncSaas: async () => {
    // Read under the effective user (impersonated / shared-admin aware) so
    // we load the right doc when admin is impersonating or a shared-admin
    // user is viewing the admin's synced state.
    const uid = getEffectiveUid();
    const appStore = useAppStore.getState();
    const saasCode = appStore.appCodeSAAS;
    const orgId = appStore.appConfig?.orgId;
    const tpaCode = appStore.appCodeTPA;
    const extensionName = appStore.extensionName;

    if (!uid || !saasCode || !orgId || !tpaCode || !extensionName) return;

    const useForPage = tpaCode === 'twilio' || tpaCode === 'whatsapp';
    const extKey = useForPage ? `${tpaCode}forpage` : extensionName;
    const docPath = `ulgebraUsers/${uid}/extensions/${extKey}/incomingSyncSaas/${saasCode}_${orgId}`;

    set({ incomingSyncSaasLoading: true });

    try {
      const snap = await getDoc(doc(db, docPath));
      if (snap.exists()) {
        const data = snap.data();
        set({
          incomingSyncSaas: {
            enabled: !!data[`ua_incoming_enable_capture_${uid}`],
            // Resolve from the authoritative server incoming-schema default
            // (e.g. Tickets for Freshdesk). Empty when unknown — never "Leads".
            targetModule:
              data['ua_incoming_saas_target_module'] ||
              getIncomingDefaultModule(useAppStore.getState().extensionName ?? undefined) ||
              '',
            autoCreateContacts: data[`ua_incoming_auto_create_contacts_${uid}`] !== false,
            uploadAttachmentToModule: data['upload_attachment_to_module_record'] !== false,
          },
          incomingSyncSaasLoading: false,
        });
      } else {
        set({ incomingSyncSaasLoading: false });
      }
    } catch (err) {
      console.error('[Settings] Failed to load incomingSyncSaas:', err);
      set({ incomingSyncSaasLoading: false });
    }
  },

  updateIncomingSyncSaas: async (updates) => {
    // Path contains {uid} — in impersonator mode, write under the
    // impersonated user's id so the admin is fixing that user's settings.
    const uid = getWriteUid();
    const appStore = useAppStore.getState();
    const saasCode = appStore.appCodeSAAS;
    const orgId = appStore.appConfig?.orgId;
    const tpaCode = appStore.appCodeTPA;
    const extensionName = appStore.extensionName;

    if (!uid || !saasCode || !orgId || !tpaCode || !extensionName) return;

    const useForPage = tpaCode === 'twilio' || tpaCode === 'whatsapp';
    const extKey = useForPage ? `${tpaCode}forpage` : extensionName;
    const docPath = `ulgebraUsers/${uid}/extensions/${extKey}/incomingSyncSaas/${saasCode}_${orgId}`;
    const current = get().incomingSyncSaas;
    const newSettings = { ...current, ...updates };

    // Optimistic update
    set({ incomingSyncSaas: newSettings });

    try {
      // Disabling — delete the document entirely so it stops being listed
      if (updates.enabled === false) {
        await deleteDoc(doc(db, docPath));
        return;
      }

      const firestoreUpdates: Record<string, unknown> = {};

      if (updates.enabled !== undefined) {
        firestoreUpdates[`ua_incoming_enable_capture_${uid}`] = updates.enabled;
        if (updates.enabled) {
          firestoreUpdates['orgId'] = orgId;
          firestoreUpdates['saasCode'] = saasCode;
        }
      }
      if (updates.targetModule !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_module'] = updates.targetModule;
      }
      if (updates.autoCreateContacts !== undefined) {
        firestoreUpdates[`ua_incoming_auto_create_contacts_${uid}`] = updates.autoCreateContacts;
      }
      if (updates.uploadAttachmentToModule !== undefined) {
        firestoreUpdates['upload_attachment_to_module_record'] = updates.uploadAttachmentToModule;
      }

      await setDoc(doc(db, docPath), firestoreUpdates, { merge: true });
    } catch (err) {
      console.error('[Settings] Failed to save incomingSyncSaas:', err);
      set({ incomingSyncSaas: current });
    }
  },

  // ── Event Incoming Capture (persisted with event-specific keys) ───

  loadEventIncomingCaptureSettings: async () => {
    const personalPath = getPreferencesDocPath();
    const orgPath = getOrgDocPath();
    if (!personalPath) {
      console.warn('[Settings] Cannot load event incoming capture: missing user/app context');
      return;
    }

    const user = useAuthStore.getState().user;
    if (!user) return;

    set({ eventIncomingCaptureLoading: true });

    try {
      // Read both org-level and personal docs, merge (personal overrides org)
      const personalSnap = await getDoc(doc(db, personalPath));
      const orgSnap = orgPath ? await getDoc(doc(db, orgPath)) : null;

      const orgData = orgSnap?.exists() ? orgSnap.data() : {};
      const personalData = personalSnap.exists() ? personalSnap.data() : {};
      const data = { ...orgData, ...personalData };

      if (Object.keys(data).length > 0) {
        const uid = user.uid;

        const enabled = !!data[`ua_incoming_enable_capture_${uid}`];
        const ignoredEventTypes = data['ua_incoming_tpa_ignored_channels'] || [];
        const ignoreNewEventTypes = !!data['ua_incoming_tpa_ignored_new_channels'];
        // Keep "absent" distinct from [] — the backend only applies the
        // new-type check when this key exists.
        const allowedEventTypes = Array.isArray(data['ua_incoming_tpa_allowed_channels'])
          ? (data['ua_incoming_tpa_allowed_channels'] as unknown[]).map(String)
          : undefined;
        const ignoreNewCustomers = !!data['ua_app_setting_incoming_no_create_new_entity'];
        const ignoreExistingCustomers = !!data['ua_app_setting_incoming_no_create_new_entity_if_exist'];
        const webhookId = data['EXISTING_WEBHOOK_ID'] || undefined;

        // Activity mapping (Pipedrive) — per-event-type keys: ua_app_setting_pp_event_type_{chanId}
        const activityTypeMapping: Record<string, string> = {};
        for (const key of Object.keys(data)) {
          if (key.startsWith('ua_app_setting_pp_event_type_')) {
            const chanId = key.replace('ua_app_setting_pp_event_type_', '');
            if (data[key]) activityTypeMapping[chanId] = String(data[key]);
          }
        }
        const createActivities = !!data['ua_app_setting_create_new_incoming_activity'];
        // Absent means delete — the behaviour every org had before this setting existed.
        const cancelledEventAction = data['ua_app_setting_cancelled_event_action'] === 'mark_cancelled'
          ? 'mark_cancelled' as const
          : 'delete' as const;

        // User mappings
        const userMappings = data['ua_incoming_event_user_mappings'] || undefined;

        // Additional options
        const stopIfExists = !!data['stop_processing_if_exist'];
        const targetModule = data['ua_incoming_saas_target_module'] || undefined;
        const enableSignalNotifications = !!data['ua_app_setting_incoming_signal_notifi_enable_for_crm'];

        // Phone patterns
        const phonePatternNoCountryCode = !!data['ua_incoming_number_fetch_without_country_code'];
        const phonePatternStartsWith0 = !!data['ua_incoming_number_fetch_starts_with_0'];
        const phonePatternStartsWithPlus = !!data['ua_incoming_number_fetch_starts_with_plus'];

        // Monday.com–specific
        const historyModule = data['ua_incoming_saas_target_event_module'] || undefined;
        const targetPhoneField = data['ua_incoming_saas_target_contact_phone_field'] || undefined;
        const targetEmailField = data['ua_incoming_saas_target_contact_email_field'] || undefined;
        const customSearchModules = Array.isArray(data['_ua_custome_search_modules_and_fields'])
          ? data['_ua_custome_search_modules_and_fields']
          : undefined;

        set({
          eventIncomingCapture: {
            enabled,
            ignoredEventTypes,
            ignoreNewEventTypes,
            allowedEventTypes,
            ignoreNewCustomers,
            ignoreExistingCustomers,
            webhookId,
            activityTypeMapping,
            createActivities,
            cancelledEventAction,
            userMappings,
            stopIfExists,
            targetModule,
            enableSignalNotifications,
            phonePatternNoCountryCode,
            phonePatternStartsWith0,
            phonePatternStartsWithPlus,
            historyModule,
            targetPhoneField,
            targetEmailField,
            customSearchModules,
          },
          eventIncomingCaptureLoading: false,
        });
      } else {
        set({ eventIncomingCaptureLoading: false });
      }
    } catch (error) {
      console.error('[Settings] Failed to load event incoming capture settings:', error);
      set({ eventIncomingCaptureLoading: false });
    }
  },

  updateEventIncomingCapture: async (updates) => {
    const docPath = getPreferencesDocPath();
    if (!docPath) {
      console.warn('[Settings] Cannot save event incoming capture: missing user/app context');
      return;
    }

    const user = useAuthStore.getState().user;
    if (!user) return;

    const uid = user.uid;
    const current = get().eventIncomingCapture;
    const newSettings = { ...current, ...updates };

    // Update local state immediately (optimistic)
    set({ eventIncomingCapture: newSettings });

    try {
      const docRef = doc(db, docPath);
      const firestoreUpdates: Record<string, unknown> = {};

      if (updates.enabled !== undefined) {
        firestoreUpdates[`ua_incoming_enable_capture_${uid}`] = updates.enabled;
      }
      if (updates.ignoredEventTypes !== undefined) {
        firestoreUpdates['ua_incoming_tpa_ignored_channels'] = updates.ignoredEventTypes;
      }
      if (updates.ignoreNewEventTypes !== undefined) {
        firestoreUpdates['ua_incoming_tpa_ignored_new_channels'] = updates.ignoreNewEventTypes;
      }
      if (updates.allowedEventTypes !== undefined) {
        firestoreUpdates['ua_incoming_tpa_allowed_channels'] = updates.allowedEventTypes;
      }
      if (updates.ignoreNewCustomers !== undefined) {
        firestoreUpdates['ua_app_setting_incoming_no_create_new_entity'] = updates.ignoreNewCustomers;
      }
      if (updates.ignoreExistingCustomers !== undefined) {
        firestoreUpdates['ua_app_setting_incoming_no_create_new_entity_if_exist'] = updates.ignoreExistingCustomers;
      }
      if (updates.webhookId !== undefined) {
        firestoreUpdates['EXISTING_WEBHOOK_ID'] = updates.webhookId;
      }
      // Activity mapping (Pipedrive) — per-event-type keys: ua_app_setting_pp_event_type_{chanId}
      if (updates.activityTypeMapping !== undefined) {
        for (const [chanId, activityType] of Object.entries(updates.activityTypeMapping)) {
          firestoreUpdates[`ua_app_setting_pp_event_type_${chanId}`] = activityType;
        }
      }
      if (updates.createActivities !== undefined) {
        firestoreUpdates['ua_app_setting_create_new_incoming_activity'] = updates.createActivities;
      }
      if (updates.cancelledEventAction !== undefined) {
        firestoreUpdates['ua_app_setting_cancelled_event_action'] = updates.cancelledEventAction;
      }
      // User mappings
      if (updates.userMappings !== undefined) {
        firestoreUpdates['ua_incoming_event_user_mappings'] = updates.userMappings;
      }
      // Additional options
      if (updates.stopIfExists !== undefined) {
        firestoreUpdates['stop_processing_if_exist'] = updates.stopIfExists;
      }
      if (updates.targetModule !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_module'] = updates.targetModule;
      }
      if (updates.enableSignalNotifications !== undefined) {
        firestoreUpdates['ua_app_setting_incoming_signal_notifi_enable_for_crm'] = updates.enableSignalNotifications;
      }
      // Phone patterns
      if (updates.phonePatternNoCountryCode !== undefined) {
        firestoreUpdates['ua_incoming_number_fetch_without_country_code'] = updates.phonePatternNoCountryCode;
      }
      if (updates.phonePatternStartsWith0 !== undefined) {
        firestoreUpdates['ua_incoming_number_fetch_starts_with_0'] = updates.phonePatternStartsWith0;
      }
      if (updates.phonePatternStartsWithPlus !== undefined) {
        firestoreUpdates['ua_incoming_number_fetch_starts_with_plus'] = updates.phonePatternStartsWithPlus;
      }
      // Monday.com–specific
      if (updates.historyModule !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_event_module'] = updates.historyModule;
      }
      if (updates.targetPhoneField !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_contact_phone_field'] = updates.targetPhoneField;
      }
      if (updates.targetEmailField !== undefined) {
        firestoreUpdates['ua_incoming_saas_target_contact_email_field'] = updates.targetEmailField;
      }
      if (updates.customSearchModules !== undefined) {
        firestoreUpdates['_ua_custome_search_modules_and_fields'] = updates.customSearchModules;
      }

      await setDoc(docRef, firestoreUpdates, { merge: true });
    } catch (error) {
      console.error('[Settings] Failed to save event incoming capture settings:', error);
      // Revert on error
      set({ eventIncomingCapture: current });
    }
  },

  updateHiddenEventMembers: async (emails) => {
    const orgPath = getOrgDocPath();
    if (!orgPath) {
      toast.error('Failed to save hidden members');
      return;
    }

    const previous = get().hiddenEventMembers;
    set({ hiddenEventMembers: emails });

    // Refresh the localStorage cache so the next page load has the new list
    // before the Firestore BG sync completes.
    try {
      const extName = useAppStore.getState().extensionName;
      if (extName) {
        localStorage.setItem(
          `ua_cache_hidden_event_members_${extName}`,
          JSON.stringify(emails)
        );
      }
    } catch { /* */ }

    try {
      await setDoc(doc(db, orgPath), { ua_hidden_event_members: emails }, { merge: true });
    } catch (error) {
      console.error('[Settings] Failed to save hidden event members:', error);
      toast.error('Failed to save hidden members');
      set({ hiddenEventMembers: previous });
    }
  },

  setSavedTemplates: (templates) => set({ savedTemplates: templates, savedTemplatesLoaded: true }),

  // ── WooCommerce Notification Rules (persisted to org doc) ──

  saveNotificationRules: (rules) => {
    set({ notificationRules: rules, notificationRulesLoaded: true });

    const orgPath = getOrgDocPath();
    if (!orgPath) {
      toast.error('Failed to save notification rules');
      return;
    }

    const docRef = doc(db, orgPath);
    setDoc(docRef, { ua_woocommerce_notification_rules: serializeForStorage(rules) }, { merge: true })
      .then(() => toast.success('Notification rules saved'))
      .catch(() => toast.error('Failed to save notification rules'));
  },

  // ── Forwarding Webhooks (persisted to personal prefs doc) ──
  // Single field write per call so concurrent edits to different
  // webhook IDs don't clobber each other (Firestore deep-merges map
  // fields when `merge: true`).

  upsertForwardingWebhook: async (webhook) => {
    // Optimistically update store first so the list updates instantly.
    set((state) => ({
      forwardingWebhooks: { ...state.forwardingWebhooks, [webhook.id]: webhook },
    }));
    const docPath = getPreferencesDocPath();
    if (!docPath) {
      toast.error('Failed to save webhook — not signed in');
      return;
    }
    try {
      await setDoc(
        doc(db, docPath),
        { FORWARDING_WEBHOOKS_LIST: { [webhook.id]: webhook } },
        { merge: true },
      );
      toast.success('Webhook saved');
    } catch (err) {
      console.error('[Settings] upsertForwardingWebhook failed:', err);
      toast.error('Failed to save webhook');
    }
  },

  removeForwardingWebhook: async (id) => {
    set((state) => {
      const next = { ...state.forwardingWebhooks };
      delete next[id];
      return { forwardingWebhooks: next };
    });
    const docPath = getPreferencesDocPath();
    if (!docPath) {
      toast.error('Failed to delete webhook — not signed in');
      return;
    }
    try {
      // `deleteField()` inside the nested map removes only this entry,
      // leaving siblings under FORWARDING_WEBHOOKS_LIST intact.
      await setDoc(
        doc(db, docPath),
        { FORWARDING_WEBHOOKS_LIST: { [id]: deleteField() } },
        { merge: true },
      );
      toast.success('Webhook deleted');
    } catch (err) {
      console.error('[Settings] removeForwardingWebhook failed:', err);
      toast.error('Failed to delete webhook');
    }
  },

  setForwardingWebhookState: async (id, state) => {
    const current = get().forwardingWebhooks[id];
    if (!current) return;
    const updated = { ...current, state };
    set((s) => ({
      forwardingWebhooks: { ...s.forwardingWebhooks, [id]: updated },
    }));
    const docPath = getPreferencesDocPath();
    if (!docPath) {
      toast.error('Failed to update webhook — not signed in');
      return;
    }
    try {
      await setDoc(
        doc(db, docPath),
        { FORWARDING_WEBHOOKS_LIST: { [id]: { state } } },
        { merge: true },
      );
    } catch (err) {
      console.error('[Settings] setForwardingWebhookState failed:', err);
      toast.error('Failed to update webhook');
    }
  },

  setTwilioSinkId: (id) => set({ uaTwilioSinkId: id }),
  setTwilioSubscriptionId: (id) => set({ uaTwilioSubscriptionId: id }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
}));

// Expose store for E2E testing — lets Playwright drive settings state
// directly (e.g. flip a master toggle to reveal its hidden sub-settings
// for a documentation screenshot) without round-tripping through network.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__settingsStore = useSettingsStore;
}

// ── Firestore cache-refresh handler — keeps trial-store in sync with admin actions ──
//
// `getDoc` is cache-first via onSnapshot: it returns the IDB-cached doc first,
// then later fires a refresh event when the server returns different data.
// loadAllSettings reads the cached doc, seeds trial-store with the cached
// trial timestamp, and resolves — but if an admin extends the trial via the
// dashboard while this tab is open, the new timestamp arrives via the refresh
// bus, NOT via re-running loadAllSettings. Without this listener, trial-store
// keeps the stale "expired" state and the paywall stays up.
//
// The handler matches the personal-prefs path:
//   uaAppPreferences/{saasCode}_orgs/{orgId}/{extensionName}/personal/{uid}
// On match, it re-extracts `ua_app_readonly_trial_start_time` and feeds it
// to trial-store. trial-store's setTrialFromSettings updates state + writes
// the new value to ua_cache_trial_start, so subsequent cold loads also see
// the fresh value.
//
// Path pattern uses 4 dynamic segments (saasCode_orgs / orgId / extensionName
// / personal / uid). Anchored at start to avoid accidental matches against
// nested template / workflow subdocs that share the same prefix.
const PERSONAL_PREFS_PATH_RE = /^uaAppPreferences\/[^/]+\/[^/]+\/[^/]+\/personal\/[^/]+$/;

onFirestoreRefresh(async (path, data) => {
  if (!PERSONAL_PREFS_PATH_RE.test(path)) return;
  if (!data) return;

  const trialStartTime = data['ua_app_readonly_trial_start_time'];
  if (typeof trialStartTime === 'number') {
    const { useTrialStore } = await import('./trial-store');
    const prev = useTrialStore.getState().trialStartTime;
    if (prev !== trialStartTime) {
      console.log(`[Settings] Firestore refresh: trial start updated ${prev} → ${trialStartTime}`);
      useTrialStore.getState().setTrialFromSettings(trialStartTime);
    }
  }
});
